import Core from '../plugin-core-system/core.class.js';
import Aria from '../helper/aria.helper.js';
import Focus from '../helper/focus.helper.js';
import Scroll from '../helper/scroll.helper.js';
import Inert from '../helper/inert.helper.js';
import Breakpoint from '../helper/breakpoint.helper.js';

export default class CsModalManager extends Core {
  static options = {
    triggerDataAttribute: 'data-cs-modal-trigger',
    triggerLabelOpen: 'data-cs-modal-trigger-label-open',
    triggerLabelClose: 'data-cs-modal-trigger-label-close',
    triggerActiveClass: 'is-opened',
    modalDataAttribute: 'data-cs-modal',
    activeClass: 'is-active',
    modalInitiallyOpenDataAttribute: 'data-cs-modal-initial-open',
    modalMaxBreakpointDataAttribute: 'data-cs-modal-max-breakpoint',
    modalMinBreakpointDataAttribute: 'data-cs-modal-min-breakpoint',
    modalBreakpointLeaveInertDataAttribute: 'data-cs-modal-breakpoint-leave-inert',
    modalContentWrapperSelector: '.js-cs-modal-content-wrapper',
    dimmingAreaDataAttribute: 'data-cs-modal-dimming-area',
    hiddenAreasDesktopDataAttribute: 'data-cs-modal-hidden-areas-desktop',
    hiddenAreasMobileDataAttribute: 'data-cs-modal-hidden-areas-mobile',
    hiddenAreasBreakpointDataAttribute: 'data-cs-modal-hidden-areas-breakpoint',
    closeSelector: '.js-cs-modal-close',
    modalFocusOnOpenDataAttribute: 'data-cs-modal-focus-on-open-selector',
    fallbackFocusSelector: '.js-cs-modal-focus-fallback'
  };

  /* ------------------------------------------------------------------
   * PUBLIC API --------------------------------------------------------
   * ---------------------------------------------------------------- */

  open(name, setFocus = true, trigger = null) {

    const modal = this.findModalByName(name);
    if (!modal) return;

    if (trigger) {
      this.lastOpenTrigger = trigger;
    }

    // check if modal is deactivated for some breakpoint
    if (
      !Breakpoint.matches(modal, CsModalManager.options.modalMaxBreakpointDataAttribute) ||
      !Breakpoint.matches(modal, CsModalManager.options.modalMinBreakpointDataAttribute)
    ) {
      return;
    }

    // close any other open modals
    this.close(false);

    this.activeModal = modal;
    this.activeModalName = name;

    CsModalManager.setModalAttributes(modal, true);
    this.setTriggerAttributes(name, true);

    // set hidden areas to inert
    this.setHiddenAreas(modal, true);

    // set focus to modal close button
    if (setFocus) {
      let focusElement = null;
      // focus to individual element via data attribute
      const focusSelector = modal.getAttribute(
        this.options.modalFocusOnOpenDataAttribute
      );
      if (focusSelector) {
        focusElement = modal.querySelector(focusSelector);
      }
      // fallback for focus: close button
      if (!focusElement) {
        const closeSelectorClass = this.options.closeSelector.replace('.', '');
        focusElement = modal.getElementsByClassName(closeSelectorClass)[0];
      }
      if (focusElement) {
        Focus.setFocusToElement(focusElement);
      } else {
        console.warn(
          `CsModalManager: kein Fokus-Element für Modal "${name}" gefunden.`
        );
      }
    }

    // add listener for background click once per modal instance
    const contentWrapper = modal.querySelector(this.options.modalContentWrapperSelector);
    if (contentWrapper && !modal.csModalBackgroundBound) {
      modal.addEventListener('click', this.handleBackgroundClick);
      modal.csModalBackgroundBound = true;
    }

    // prevent body from scrolling
    Scroll.toggleScrollBehavior(this.body, false);

    // add individual dimming areas
    this.bindDimmingArea(modal);

    this.dispatchModalEvent('open', modal, name);
  }

  close(returnFocusToOpenTrigger = true, closeTrigger = null) {

    if (!this.activeModal) return;

    // activate body scrolling
    Scroll.toggleScrollBehavior(this.body);

    CsModalManager.setModalAttributes(this.activeModal, false);
    this.setTriggerAttributes(this.activeModalName, false);

    // remove inert from hidden areas
    this.setHiddenAreas(this.activeModal, false);

    // remove individual dimming areas
    this.unbindDimmingArea();

    // return focus to its trigger element
    if (returnFocusToOpenTrigger) {
      const openTriggerElement = this.lastOpenTrigger;
      if (openTriggerElement) {
        Focus.setFocusToElement(openTriggerElement);
      } else {
        const defaultFocusElement = document.querySelector(this.options.fallbackFocusSelector);

        if (defaultFocusElement) {
          Focus.setFocusToElement(defaultFocusElement);
        } else {
          console.warn(
            `CsModalManager: weder Trigger noch Fallback (${this.options.fallbackFocusSelector}) sichtbar.`
          );
        }
      }
    }

    // remove background click listener if it was attached
    if (this.activeModal.csModalBackgroundBound) {
      this.activeModal.removeEventListener('click', this.handleBackgroundClick);
      delete this.activeModal.csModalBackgroundBound;
    }

    this.dispatchModalEvent('close', this.activeModal, this.activeModalName, closeTrigger);

    this.activeModal = null;
    this.activeModalName = null;
  }

  toggle(name, trigger = null) {
    (this.activeModalName === name) ? this.close(true, trigger) : this.open(name, true, trigger);
  }

  /* ------------------------------------------------------------------
   * BINDINGS ----------------------------------------------------------
   * ---------------------------------------------------------------- */

  bindTriggers() {
    this.triggers = Array.from(
      this.el.querySelectorAll(`[${this.options.triggerDataAttribute}]`)
    );

    this.triggers.forEach(trigger => {
      this.setInitialTriggerLabel(trigger);

      trigger.csModalHandler = (e) => {
        e.preventDefault();
        const name = trigger.dataset.csModalTrigger;
        if (name) this.toggle(name, trigger);
      };
      trigger.addEventListener('click', trigger.csModalHandler);
    });
  }

  bindCloseSelectors(container = this.el) {
    this.closeSelectors = Array.from(
      container.querySelectorAll(this.options.closeSelector)
    );

    this.closeSelectors.forEach(closeSelector => {
      if (closeSelector.csModalHandler) return; // prevent double binding

      closeSelector.csModalHandler = (e) => {
        e.preventDefault();
        this.close(true, closeSelector);
      };

      closeSelector.addEventListener('click', closeSelector.csModalHandler);
    });
  }

  /* ------------------------------------------------------------------
   * SETUP -------------------------------------------------------------
   * ---------------------------------------------------------------- */

  setInitialModalAttributes() {
    this.modals = this.getAllModals();

    this.modals.forEach(modal => {
      const shouldBeInitialized = Breakpoint.matches(modal, CsModalManager.options.modalMaxBreakpointDataAttribute) && Breakpoint.matches(modal, CsModalManager.options.modalMinBreakpointDataAttribute);

      if (shouldBeInitialized) {
        // initially set modal to inert
        Inert.setInertAttr(modal);
      }
    });
  }

  openModalOnInit() {
    // check for initially open modal
    const modals = this.getAllModals();
    const initialOpenModals = modals.filter(modal =>
      modal.hasAttribute(CsModalManager.options.modalInitiallyOpenDataAttribute)
    );

    if (initialOpenModals.length > 1) {
      console.warn(
        `CsModalManager: Mehr als ein Modal hat das Attribut "${CsModalManager.options.modalInitiallyOpenDataAttribute}". Es wird nur das erste geöffnet.`);
    }

    if (initialOpenModals.length == 1) {
      const firstModal = initialOpenModals[0];
      const name = firstModal.getAttribute(this.options.modalDataAttribute);
      if (name) {
        setTimeout(() => this.open(name), 0);
      }
    }
  }

  /* ------------------------------------------------------------------
   * ATTRIBUTE HELPERS -------------------------------------------------
   * ---------------------------------------------------------------- */

  static setModalAttributes(modal, shouldBeOpened) {
    if (shouldBeOpened) {
      modal.classList.add(CsModalManager.options.activeClass);
      Inert.setInertAttr(modal, false);
    } else {
      modal.classList.remove(CsModalManager.options.activeClass);
      Inert.setInertAttr(modal);
    }
  }

  static destroyModalAttributes(modal) {
    modal.classList.remove(CsModalManager.options.activeClass);
    if (modal.getAttribute(this.options.modalBreakpointLeaveInertDataAttribute) != '1') {
      Inert.setInertAttr(modal, false);
    }
  }

  setInitialTriggerLabel(trigger) {
    const openLabel = trigger.getAttribute(this.options.triggerLabelOpen);
    if (openLabel) this.setLabelAttributes(trigger, openLabel);
  }

  setTriggerAttributes(name, shouldBeOpened) {
    const triggers = this.triggers.filter(t => t.dataset.csModalTrigger === name);
    if (triggers.length === 0) return;

    triggers.forEach(trigger => {
      trigger.classList.toggle(this.options.triggerActiveClass, shouldBeOpened);

      if (trigger.hasAttribute('aria-expanded')) {
        Aria.setExpanded(trigger, shouldBeOpened);
      }

      const openLabel = trigger.getAttribute(this.options.triggerLabelOpen);
      const closeLabel = trigger.getAttribute(this.options.triggerLabelClose);
      if (!openLabel || !closeLabel) return;
      this.setLabelAttributes(trigger, shouldBeOpened ? closeLabel : openLabel);
    });
  }

  setLabelAttributes(trigger, label) {
    trigger.setAttribute('aria-label', label);
    trigger.setAttribute('title', label);
  }

  /* ------------------------------------------------------------------
   * HIDDEN AREA HANDLING ----------------------------------------------
   * ---------------------------------------------------------------- */

  setHiddenAreas(modal, hideAreas = true) {
    // always release previous inerted areas first
    if (this.inertedAreas) {
      this.inertedAreas.forEach(el => {
        // modal, which remains closed, must remain inert
        if (!this.isClosedModal(el)) {
          Inert.setInertAttr(el, false);
        }
      });
      this.inertedAreas = null;
    }

    const hiddenAreasValue = this.getHiddenAreasJSON(modal);
    if (!hiddenAreasValue) return;

    if (!hideAreas) return;

    if (hideAreas) {
      this.inertedAreas = [];

      let hiddenAreaSelectors;
      try {
        hiddenAreaSelectors = JSON.parse(hiddenAreasValue);
      } catch {
        return;
      }

      hiddenAreaSelectors.forEach(hiddenAreaSelector => {
        document.querySelectorAll(hiddenAreaSelector).forEach(el => {
          Inert.setInertAttr(el);
          this.inertedAreas.push(el);
        });
      });
    }
  }

  getHiddenAreasJSON(modal) {
    const hiddenAreasValue = this.isMobile(modal)
      ? this.options.hiddenAreasMobileDataAttribute
      : this.options.hiddenAreasDesktopDataAttribute;

    return modal.getAttribute(hiddenAreasValue);
  }

  /* ---------------------------------------------------------------
 * DIMMING-AREA HANDLING
 * ------------------------------------------------------------- */

  bindDimmingArea(modal) {
    this.unbindDimmingArea();

    const selector = modal.getAttribute(this.options.dimmingAreaDataAttribute);
    if (!selector) return;

    const el = document.querySelector(selector);
    if (!el) {
      console.warn(`CsModalManager: dimming element "${selector}" not gefunden.`);
      return;
    }

    const handler = () => this.close(false, el);
    el.addEventListener('click', handler);

    el.classList.add(this.options.activeClass);

    this.dimmingAreaEl = el;
    this.dimmingAreaEl.csModalDimmingHandler = handler;
  }

  unbindDimmingArea() {
    if (!this.dimmingAreaEl) return;

    this.dimmingAreaEl.removeEventListener(
      'click',
      this.dimmingAreaEl.csModalDimmingHandler
    );
    delete this.dimmingAreaEl.csModalDimmingHandler;

    this.dimmingAreaEl.classList.remove(this.options.activeClass);
    this.dimmingAreaEl = null;
  }

  /* ------------------------------------------------------------------
   * BREAKPOINT UTILS --------------------------------------------------
   * ---------------------------------------------------------------- */

  isMobile(modal) {
    return Breakpoint.matches(modal, this.options.hiddenAreasBreakpointDataAttribute);
  }

  isClosedModal(modal) {
    return modal.hasAttribute(this.options.modalDataAttribute) &&
      Breakpoint.matches(modal, this.options.modalMaxBreakpointDataAttribute) &&
      Breakpoint.matches(modal, this.options.modalMinBreakpointDataAttribute) &&
      !modal.classList.contains(this.options.activeClass);
  }

  /* ------------------------------------------------------------------
   * EVENTS -------------------------------------------------------------
   * ---------------------------------------------------------------- */

  dispatchModalEvent(type, modal, name, triggerLink = null, goToMode = null) {
    const evt = new CustomEvent(`cs-modal-${type}`, {
      detail: {name, modal, triggerLink, goToMode},
      bubbles: true,
      composed: true
    });

    document.dispatchEvent(evt);
  }

  /* ------------------------------------------------------------------
   * HELPERS -----------------------------------------------------------
   * ---------------------------------------------------------------- */

  findModalByName(name) {
    return this.el.querySelector(`[${this.options.modalDataAttribute}="${name}"]`);
  }

  static getFirstTriggerLinkFromModalName(modalName) {
    return document.querySelectorAll(`[${CsModalManager.options.triggerDataAttribute}="${modalName}"]`)[0];
  }

  getAllModals() {
    return Array.from(
      document.querySelectorAll(`[${this.options.modalDataAttribute}]`)
    );
  }

  handleKeydown(e) {
    switch (e.key) {
      case 'Escape':
        if (this.activeModal) {
          e.preventDefault();
          this.close();
        }
        break;
      case ' ':
      case 'Spacebar':
      case 'ArrowDown': {
        const activeEl = document.activeElement;
        const isModalTrigger = activeEl?.hasAttribute(this.options.triggerDataAttribute);

        if (isModalTrigger) {
          e.preventDefault();
          this.dispatchModalEvent('keyevent-on-trigger-to-open-modal', null, null, document.activeElement);
        }
        break;
      }
      case 'ArrowLeft':
      case 'ArrowRight':
        if (this.activeModal) {
          e.preventDefault();
          this.dispatchModalEvent('keyevent-on-trigger-to-close-modal', this.activeModal, this.activeModalName, document.activeElement, e.key === 'ArrowLeft' ? 'previous' : 'next');
        }
        break;
    }
  }

  handleBackgroundClick(e) {
    const contentWrapper = e.currentTarget.querySelector(this.options.modalContentWrapperSelector);
    if (!contentWrapper) return;

    if (!contentWrapper.contains(e.target)) {
      this.close(true, e.target);
    }
  }

  handleResize() {
    const modalsWithBreakpoints = Array.from(
      document.querySelectorAll(`[${CsModalManager.options.modalMaxBreakpointDataAttribute}],` + `[${CsModalManager.options.modalMinBreakpointDataAttribute}]`)
    );

    modalsWithBreakpoints.forEach(modal => {
      const inRange =
        Breakpoint.matches(modal, CsModalManager.options.modalMaxBreakpointDataAttribute) &&
        Breakpoint.matches(modal, CsModalManager.options.modalMinBreakpointDataAttribute);

      if (inRange) {
        if (modal !== this.activeModal) {
          CsModalManager.setModalAttributes(modal, false);
        }
      } else {
        if (this.activeModal === modal) {
          this.close(false);
        }
        CsModalManager.destroyModalAttributes(modal);
      }
    });

    if (this.activeModal) {
      this.setHiddenAreas(this.activeModal, true);
    }
  }

  /* ------------------------------------------------------------------
   * LIFECYCLE ---------------------------------------------------------
   * ---------------------------------------------------------------- */

  init() {
    this.body = document.body;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleBackgroundClick = this.handleBackgroundClick.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.activeModal = null;
    this.activeModalName = null;
    this.lastOpenTrigger = null;

    this.setInitialModalAttributes();
    this.bindTriggers();
    this.bindCloseSelectors();

    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize);

    this.openModalOnInit();
  }
}
